import React, { useEffect, useState } from "react";
import { X, ShareNetwork, Plus } from "@phosphor-icons/react";

const DISMISSED_KEY = "qd_install_prompt_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  // iOS / Android standalone detection
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function recentlyDismissed() {
  try {
    const t = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    if (!t) return false;
    return Date.now() - t < DISMISS_TTL_MS;
  } catch { return false; }
}

export default function InstallPrompt() {
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("hidden"); // "android" | "ios" | "hidden"

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Android / desktop Chrome / Edge — beforeinstallprompt
    const onBefore = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
      setMode("android");
      // Delay a beat so it doesn't fire instantly on first paint
      setTimeout(() => setVisible(true), 1500);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // After install — hide & clean up
    const onInstalled = () => {
      setVisible(false);
      setDeferredEvt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari has no beforeinstallprompt — show hint after 5s if user is on iOS
    if (isIOS() && !isStandalone()) {
      const t = setTimeout(() => {
        setMode("ios");
        setVisible(true);
      }, 5000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBefore);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch (err) { /* ignore */ void err; }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredEvt) return;
    deferredEvt.prompt();
    const choice = await deferredEvt.userChoice.catch(() => null);
    if (choice?.outcome !== "accepted") dismiss();
    setVisible(false);
    setDeferredEvt(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-24 left-3 right-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60]"
    >
      <div className="qd-card p-4 shadow-2xl" style={{ borderColor: "rgba(255,30,30,0.35)" }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="qd-overline mb-1" style={{ color: "#E55A5A" }}>Install Q Drives</div>
            {mode === "android" && (
              <div className="text-[13px] text-white/85 leading-snug">
                Get a faster, app-like experience. Installs in seconds, no app store required.
              </div>
            )}
            {mode === "ios" && (
              <div className="text-[13px] text-white/85 leading-snug">
                Add Q Drives to your Home Screen.<br />
                Tap{" "}<ShareNetwork size={14} weight="bold" className="inline align-middle text-white" />
                {" "}in Safari, then{" "}
                <span className="inline-flex items-center gap-0.5 align-middle text-white">
                  <Plus size={12} weight="bold" /> Add to Home Screen
                </span>.
              </div>
            )}
          </div>
          <button
            data-testid="install-prompt-close"
            onClick={dismiss}
            className="text-white/55 hover:text-white shrink-0 p-1"
            aria-label="Dismiss"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
        {mode === "android" && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button data-testid="install-prompt-install" onClick={install} className="btn-primary text-xs py-2">
              Install
            </button>
            <button onClick={dismiss} className="btn-secondary text-xs py-2">
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
